/**
 * Turns ledger events into the items the conversation view renders.
 * Pure: no React, no network.
 */

export function buildTimeline(events) {
  const items = [];
  const steps = new Map();
  const ordered = [...(events || [])].sort((a, b) => a.seq - b.seq);

  for (const event of ordered) {
    const payload = event.payload || {};
    const base = { key: `e${event.seq}`, seq: event.seq, runId: event.runId, at: event.createdAt };

    switch (event.type) {
      case 'USER_MESSAGE':
        items.push({ ...base, kind: 'user', text: payload.text || '', contextTag: payload.contextTag || null, pageContext: payload.pageContext || null });
        break;
      case 'ASSISTANT_MESSAGE':
        if (payload.text && payload.text.trim()) {
          items.push({ ...base, kind: 'assistant', text: payload.text, limitReached: !!payload.limitReached });
        }
        break;
      case 'TOOL_CALL': {
        const step = {
          ...base,
          kind: 'step',
          toolCallId: payload.toolCallId,
          name: payload.name,
          toolKind: payload.kind,
          args: payload.args || {},
          status: 'running',
          result: undefined,
          error: null,
          approval: null,
          skill: null,
        };
        steps.set(payload.toolCallId, step);
        items.push(step);
        break;
      }
      case 'TOOL_RESULT': {
        const step = steps.get(payload.toolCallId);
        if (!step) break;
        step.status = payload.isError ? 'error' : 'done';
        step.error = payload.isError ? payload.error || payload.content : null;
        step.result = payload.result;
        step.resultTooLarge = !!payload.resultTooLarge;
        step.resultSeq = event.seq;
        step.finishedAt = event.createdAt;
        break;
      }
      case 'APPROVAL_REQUESTED': {
        const step = steps.get(payload.toolCallId);
        if (step) {
          step.approval = payload;
          step.status = step.status === 'running' ? 'awaiting' : step.status;
        } else {
          items.push({ ...base, kind: 'approval-orphan', approval: payload });
        }
        break;
      }
      case 'SKILL_LOADED': {
        const step = [...steps.values()].reverse().find((candidate) => candidate.name === 'load_skill');
        if (step) step.skill = payload.slug;
        break;
      }
      case 'APPROVAL_DECISION': {
        const approved = (payload.approved || []).length;
        const rejected = (payload.rejected || []).length;
        items.push({
          ...base,
          kind: 'note',
          tone: 'decision',
          text: payload.reason
            ? `Not approved: ${payload.reason.toLowerCase()}`
            : `${payload.decidedBy ? `${payload.decidedBy} approved` : 'Approved'} ${approved}${rejected ? `, rejected ${rejected}` : ''}`,
        });
        break;
      }
      case 'UNDO_EXECUTED':
        items.push({ ...base, kind: 'note', tone: 'undo', text: `Undone: ${payload.summary}` });
        break;
      case 'UNDO_FAILED':
        items.push({ ...base, kind: 'note', tone: 'warning', text: `Could not undo "${payload.summary}": ${payload.error}` });
        break;
      case 'RUN_COMPLETED':
        items.push({ ...base, kind: 'run-summary', timeSaved: payload.timeSaved || null, durationMs: payload.durationMs, steps: payload.steps });
        break;
      case 'RUN_FAILED':
        items.push({ ...base, kind: 'run-failed', error: payload.error || 'The run failed', timeSaved: payload.timeSaved || null });
        break;
      case 'RUN_CANCELLED':
        items.push({ ...base, kind: 'run-cancelled', text: payload.error || 'Stopped' });
        break;
      default:
        break;
    }
  }
  return items;
}

/** Status of each step for the "live" indicator. */
export function lastRunState(events) {
  const ordered = [...(events || [])].sort((a, b) => a.seq - b.seq);
  for (let i = ordered.length - 1; i >= 0; i -= 1) {
    const type = ordered[i].type;
    if (type === 'RUN_COMPLETED' || type === 'RUN_FAILED' || type === 'RUN_CANCELLED') return 'finished';
    if (type === 'APPROVAL_REQUESTED') return 'awaiting';
    if (type === 'USER_MESSAGE' || type === 'TOOL_CALL' || type === 'TOOL_RESULT' || type === 'ASSISTANT_MESSAGE' || type === 'RUN_STARTED') {
      return 'working';
    }
  }
  return 'idle';
}

export function mergeEvents(current, incoming) {
  const bySeq = new Map((current || []).map((event) => [event.seq, event]));
  for (const event of incoming || []) bySeq.set(event.seq, event);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}
